/**
 * §7.10 Genomic Surveillance. Two-strain (wild-type + emerging variant)
 * co-circulation tracker. The variant is generated synthetically from
 * the bundled Tier 2 reference genome by introducing a configurable
 * SNV burden; the SNV burden + transmission-advantage slider determine
 * the variant's R0 multiplier.
 *
 * Engine plumbing exercised:
 *   - simulateCoCirculation (first UI use)
 *   - loadBundledGenome + needlemanWunsch + callVariants (variant generation)
 *   - Cross-immunity slider (sets crossImmunity_AB = crossImmunity_BA)
 *
 * What the surface answers:
 *   - When does the variant overtake the wild-type? (lineage-frequency curve)
 *   - How does cross-immunity blunt the second wave?
 *   - What's the cumulative attack rate per lineage at horizon?
 */

import { useMemo, useState } from 'react';
import { findPathogen } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateCoCirculation } from '@biomax/plugin-epidemiology';
import { hasBundledGenome, loadBundledGenome, GENOME_REGISTRY } from '@biomax/plugin-pathogen-genomics';
import { needlemanWunsch, callVariants } from '@biomax/plugin-bioseq';
import { Sparkline } from '../sparkline';

const ANALYSIS_REGION_BP = 400;
const ANALYSIS_OFFSET_BP = 800;

export function GenomicSurveillance() {
  const regions = listRegions();
  const bundledIds = Object.keys(GENOME_REGISTRY).filter(hasBundledGenome);

  const [pathogenId, setPathogenId] = useState<string>(bundledIds[0] ?? 'sars-cov-2-wildtype');
  const [regionId, setRegionId] = useState<string>('usa');
  const [snvBurden, setSnvBurden] = useState<number>(15);
  const [transmissionAdvantage, setTransmissionAdvantage] = useState<number>(40);
  const [crossImmunity, setCrossImmunity] = useState<number>(0.5);
  const [seedDelayDays, setSeedDelayDays] = useState<number>(30);

  const view = useMemo(() => {
    if (!hasBundledGenome(pathogenId)) return null;
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;

    // Synthetic variant: introduce snvBurden SNVs into a window of the reference.
    const genome = loadBundledGenome(pathogenId);
    const ref = genome.sequence.slice(ANALYSIS_OFFSET_BP, ANALYSIS_OFFSET_BP + ANALYSIS_REGION_BP);
    let seed = 12345;
    const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 0x100000000; };
    let variantSeq = ref;
    for (let i = 0; i < snvBurden; i++) {
      const pos = Math.floor(rand() * variantSeq.length);
      const cur = variantSeq[pos]!;
      const alts = ['A', 'C', 'G', 'T'].filter(b => b !== cur);
      const alt = alts[Math.floor(rand() * alts.length)]!;
      variantSeq = variantSeq.slice(0, pos) + alt + variantSeq.slice(pos + 1);
    }
    const alignment = needlemanWunsch(ref, variantSeq);
    const calledVariants = callVariants(alignment);

    // Engine params: wild-type vs variant.
    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const r0Wild = pathogen.R0.value;
    const r0Variant = r0Wild * (1 + transmissionAdvantage / 100);
    const betaWild = r0Wild * gamma;
    const betaVariant = r0Variant * gamma;
    const N = region.population.total;
    const duration = 200;

    // Variant seeded with a delay: simulateCoCirculation seeds both at t=0, but we
    // model the variant delay by starting it with much smaller I0_B and letting it
    // grow against the wild-type wave (consistent with how Omicron emerged in late 2021).
    const sim = simulateCoCirculation({
      N,
      I0_A: 500, I0_B: Math.max(1, Math.floor(500 / Math.pow(2, seedDelayDays / 5))),
      beta_A: betaWild, beta_B: betaVariant,
      sigma_A: sigma, sigma_B: sigma,
      gamma_A: gamma, gamma_B: gamma,
      crossImmunity_AB: crossImmunity, crossImmunity_BA: crossImmunity,
      durationDays: duration, dtDays: 1,
    });

    // Lineage frequency = I_A / (I_A + I_B) at each timestep (active-infection share).
    const I_A = sim.compartments.I_A!;
    const I_B = sim.compartments.I_B!;
    const wildShare: number[] = [];
    const variantShare: number[] = [];
    let crossoverDay: number | null = null;
    for (let i = 0; i < I_A.length; i++) {
      const total = I_A[i]! + I_B[i]!;
      const w = total > 0 ? I_A[i]! / total : 0;
      const v = total > 0 ? I_B[i]! / total : 0;
      wildShare.push(w);
      variantShare.push(v);
      if (crossoverDay === null && v > w && total > 100) crossoverDay = i;
    }

    const finalR_A = sim.compartments.R_A!.at(-1)!;
    const finalR_B = sim.compartments.R_B!.at(-1)!;
    const finalR_AB = sim.compartments.R_AB!.at(-1)!;
    const attackWild = (finalR_A + finalR_AB) / N;
    const attackVariant = (finalR_B + finalR_AB) / N;

    return {
      pathogen, region, sim, calledVariants,
      ref, variantSeq, alignment,
      r0Wild, r0Variant,
      wildShare, variantShare, crossoverDay,
      attackWild, attackVariant,
    };
  }, [pathogenId, regionId, snvBurden, transmissionAdvantage, crossImmunity, seedDelayDays]);

  if (!view) return <div>Loading… (this surface requires a bundled Tier 2 reference genome)</div>;

  const peakI_A = Math.max(...view.sim.compartments.I_A!);
  const peakI_B = Math.max(...view.sim.compartments.I_B!);
  const peakIDay_A = view.sim.compartments.I_A!.indexOf(peakI_A);
  const peakIDay_B = view.sim.compartments.I_B!.indexOf(peakI_B);

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.10</span>
        <h1 className="surface__title">Genomic Surveillance · lineage frequency tracker</h1>
      </div>
      <div className="surface__sub">
        Two-strain co-circulation · synthetic variant generated from {view.pathogen.names.common} reference{' '}
        ({GENOME_REGISTRY[pathogenId]?.refseq}) ·{' '}
        <span className="receipt"><span className="receipt__dot" />engine {view.sim.receipt.engineVersion} · {view.sim.receipt.elapsedMs.toFixed(0)} ms · {view.sim.receipt.steps} steps</span>
      </div>

      <div className="panel panel--accent" style={{ marginBottom: 'var(--bm-space-4)' }}>
        <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-xs)', color: 'var(--bm-text-muted)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--bm-signal)' }}>SYNTHETIC SCENARIO — not surveillance data.</strong> The "variant"
          is generated by introducing user-chosen mutations into the real bundled reference genome to demonstrate the
          variant-calling + co-circulation engine. It is NOT an observed lineage. Real lineage frequencies are in the
          §7.1 live feed (CoV-Spectrum LAPIS).
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen (Tier 2 bundled)</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {bundledIds.map(id => <option key={id} value={id}>{id} · {GENOME_REGISTRY[id]?.refseq}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
        </div>
        <div className="simctl" style={{ marginTop: 12 }}>
          <label className="simctl__field">
            <span className="simctl__label">SNV burden · {snvBurden} mutations / {ANALYSIS_REGION_BP} bp</span>
            <input type="range" min="0" max="60" step="1" value={snvBurden}
              onChange={e => setSnvBurden(parseInt(e.target.value, 10))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Variant R0 advantage · +{transmissionAdvantage}%</span>
            <input type="range" min="-20" max="120" step="5" value={transmissionAdvantage}
              onChange={e => setTransmissionAdvantage(parseInt(e.target.value, 10))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Cross-immunity · {(crossImmunity * 100).toFixed(0)}%</span>
            <input type="range" min="0" max="1" step="0.05" value={crossImmunity}
              onChange={e => setCrossImmunity(parseFloat(e.target.value))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Variant seed delay · {seedDelayDays} days</span>
            <input type="range" min="0" max="90" step="1" value={seedDelayDays}
              onChange={e => setSeedDelayDays(parseInt(e.target.value, 10))} />
          </label>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Crossover day</div>
          <div className="kpi__value kpi__value--signal">
            {view.crossoverDay !== null ? `Day ${view.crossoverDay}` : 'no crossover'}
          </div>
          <div className="kpi__sub">variant overtakes wild-type</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Wild-type R0 / peak</div>
          <div className="kpi__value">{view.r0Wild.toFixed(2)}</div>
          <div className="kpi__sub">{(peakI_A / 1e6).toFixed(2)}M day {peakIDay_A}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Variant R0 / peak</div>
          <div className="kpi__value kpi__value--danger">{view.r0Variant.toFixed(2)}</div>
          <div className="kpi__sub">{(peakI_B / 1e6).toFixed(2)}M day {peakIDay_B}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Wild-type attack rate</div>
          <div className="kpi__value">{(view.attackWild * 100).toFixed(0)}%</div>
          <div className="kpi__sub">R_A + R_AB at horizon</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Variant attack rate</div>
          <div className="kpi__value kpi__value--danger">{(view.attackVariant * 100).toFixed(0)}%</div>
          <div className="kpi__sub">R_B + R_AB at horizon · {view.calledVariants.length} SNVs called</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Lineage frequency (active infections)</span>
          <span className="panel__meta">I_A / (I_A + I_B) vs I_B / (I_A + I_B)</span>
        </div>
        <Sparkline values={view.wildShare} label="Wild-type share" stroke="var(--bm-signal)" />
        <Sparkline values={view.variantShare} label="Variant share" stroke="var(--bm-danger)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Infectious compartment trajectories</span>
        </div>
        <Sparkline values={view.sim.compartments.I_A!} label="Wild-type (I_A)" stroke="var(--bm-signal)" />
        <Sparkline values={view.sim.compartments.I_B!} label="Variant (I_B)" stroke="var(--bm-danger)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Method + caveats</span>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          The variant sequence is generated <strong>synthetically</strong> from a {ANALYSIS_REGION_BP} bp
          window of the bundled reference by introducing {snvBurden} random SNVs (deterministic
          seed for reproducibility). Its R0 is set as a percentage advantage over the
          wild-type — this is illustrative, not a fitness inference from sequence. v0.4
          will swap the synthetic-SNV input for real lineage data (Nextstrain-style
          empirical R0 + frequency-trajectory feed).
          <br /><br />
          <em>Note:</em> the cross-immunity slider sets both crossImmunity_AB +
          crossImmunity_BA to the same value (symmetric). A real Omicron-vs-Delta
          configuration would be asymmetric — prior Delta infection provides moderate
          cross-protection against Omicron, but prior Omicron provides very weak
          cross-protection against Delta.
        </div>
      </div>
    </div>
  );
}
